import type { AccommodationRate } from '@data-access/graphql/schema.types'
import type { AvailableRoom } from '@feat/feat-accommodation/availability'

import { INITIAL_ACTIVE_ROOM, useRoomPickerStore } from './room-picker-store'
import type { ActiveRoomState, PaxAllocation, RoomConfig } from './room-picker-types'

export const setActiveRoom = (update: Partial<ActiveRoomState>) => {
  useRoomPickerStore.setState(state => ({
    ...state,
    activeRoom: { ...state.activeRoom, ...update },
  }))
}

export const updateRoom = (allocationId: string, roomConfig: RoomConfig) => {
  useRoomPickerStore.setState(state => {
    const roomConfigs = new Map(state.roomConfigs)
    roomConfigs.set(allocationId, roomConfig)
    return { ...state, roomConfigs }
  })
}

export const appendRoom = (allocation: PaxAllocation) => {
  useRoomPickerStore.setState(state => {
    const roomConfigs = new Map(state.roomConfigs)
    roomConfigs.set(allocation.id, {})
    return {
      ...state,
      roomConfigs,
      activeRoom: { allocationId: allocation.id, slideDirection: 1 },
      allocation: [...(state.allocation ?? []), allocation],
    }
  })
}

export const removeRoom = (allocationId: string) => {
  const currentRoomConfigs = useRoomPickerStore.getState().roomConfigs
  if (!currentRoomConfigs.has(allocationId)) return

  useRoomPickerStore.setState(state => {
    const roomConfigs = new Map(state.roomConfigs)
    roomConfigs.delete(allocationId)

    const currentActiveId = state.activeRoom.allocationId
    const isRemovingActive = currentActiveId === allocationId

    const newActiveId = isRemovingActive
      ? (roomConfigs.keys().next().value ?? null)
      : currentActiveId

    const newAllocation = state.allocation?.filter(a => a.id !== allocationId)

    return {
      ...state,
      roomConfigs,
      activeRoom: { allocationId: newActiveId, slideDirection: 0 },
      allocation: !newAllocation?.length ? null : newAllocation,
    }
  })
}

export const setRoomConfigs = (allocations: PaxAllocation[] | null | undefined) => {
  const newMap = new Map<string, RoomConfig>()
  for (const alloc of allocations ?? []) {
    newMap.set(alloc.id, {})
  }

  useRoomPickerStore.setState({
    roomConfigs: newMap,
    activeRoom: {
      allocationId: newMap.keys().next().value ?? null,
      slideDirection: 0,
    },
    allocation: allocations ?? null,
  })
}

export const resetRoomConfig = (allocationId: string) => {
  useRoomPickerStore.setState(state => {
    const roomConfigs = new Map(state.roomConfigs)
    roomConfigs.set(allocationId, {})
    return { ...state, roomConfigs }
  })
}

export const resetRoomPickerStore = () => {
  useRoomPickerStore.setState({
    roomConfigs: new Map<string, RoomConfig>(),
    activeRoom: INITIAL_ACTIVE_ROOM,
    isRoomPaxModalOpen: false,
    allocation: null,
    selectedRate: null,
    selectedRoom: null,
  })
}

export const resetSelectedRoomRate = () => {
  useRoomPickerStore.setState({ selectedRoom: null, selectedRate: null })
}

export const setSelectedRoomRate = (room: AvailableRoom, rate: AccommodationRate) => {
  useRoomPickerStore.setState({ selectedRoom: room, selectedRate: rate })
}

export const setIsRoomPaxModalOpen = (isOpen: boolean) => {
  useRoomPickerStore.setState({ isRoomPaxModalOpen: isOpen })
}

export const updateAllocation = (allocationId: string, allocation: PaxAllocation) => {
  useRoomPickerStore.setState(state => {
    const updatedAllocation = state.allocation?.map(a => (a.id === allocationId ? allocation : a))
    return { ...state, allocation: updatedAllocation }
  })
}
